/**
 * AuthGate — the register/login screen shown before the game (login is required to play).
 * Two ribbons: "Log In" (default) and "Register". Username + password only — no email,
 * no confirmation (see net/auth.ts for how username maps to Supabase under the hood).
 */
import { useState, type FormEvent } from "react";
import { register, login, type Account } from "@/net/auth";
import { isSupabaseConfigured } from "@/net/supabase";
import { t, useLang } from "@/i18n";

interface Props {
  onAuthed: (account: Account) => void;
}

const LOGO_SRC = `${import.meta.env.BASE_URL}logo.png`;

export default function AuthGate({ onAuthed }: Props) {
  useLang();
  const [tab, setTab] = useState<"login" | "register">("login"); // login shown first
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logoOk, setLogoOk] = useState(true);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    const res = tab === "login" ? await login(username, password) : await register(username, password);
    setBusy(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    if (res.account) onAuthed(res.account);
  };

  const switchTab = (next: "login" | "register"): void => {
    setTab(next);
    setError(null);
  };

  return (
    <div className="auth-gate">
      {logoOk ? (
        <img className="auth-logo" src={LOGO_SRC} alt="Space Adventura" onError={() => setLogoOk(false)} />
      ) : (
        <div className="auth-wordmark" role="img" aria-label="Space Adventura">
          <span className="auth-word auth-word--top">SPACE</span>
          <span className="auth-word auth-word--bot">ADVENTURA</span>
        </div>
      )}
      <div className="auth-card">
        {!isSupabaseConfigured && (
          <div className="auth-config-note">
            {t("Online play isn't configured yet — add your Supabase URL and anon key to .env, then restart.")}
          </div>
        )}

        <div className="auth-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "login"}
            className={`auth-tab${tab === "login" ? " on" : ""}`}
            onClick={() => switchTab("login")}
          >
            {t("Log In")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "register"}
            className={`auth-tab${tab === "register" ? " on" : ""}`}
            onClick={() => switchTab("register")}
          >
            {t("Register")}
          </button>
        </div>

        <form className="auth-form" onSubmit={submit}>
          <input
            className="auth-input"
            type="text"
            autoComplete="username"
            placeholder={t("Username")}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
            maxLength={20}
          />
          <input
            className="auth-input"
            type="password"
            autoComplete={tab === "login" ? "current-password" : "new-password"}
            placeholder={t("Password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error && <div className="auth-error">{error}</div>}
          <button className="auth-submit" type="submit" disabled={busy || !isSupabaseConfigured}>
            {busy ? t("Please wait…") : tab === "login" ? t("Log In") : t("Create Account")}
          </button>
        </form>

        <div className="auth-hint">
          {tab === "login" ? t("New pilot? Switch to Register.") : t("Already have an account? Switch to Log In.")}
        </div>
      </div>
    </div>
  );
}
